import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Enhanced shimmer skeleton with gradient animation
 */
const ShimmerSkeleton = ({ className = '' }) => (
  <div 
    className={`relative overflow-hidden bg-gray-200 rounded ${className}`}
    style={{
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite'
    }}
  />
);

/**
 * Skeleton loader for dashboard tool cards
 */
export const ToolCardSkeleton = () => (
  <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
    <div className="flex flex-col items-center text-center">
      {/* Icon placeholder */}
      <ShimmerSkeleton className="w-12 h-12 rounded-xl mb-3" />
      {/* Title placeholder */}
      <ShimmerSkeleton className="h-4 w-24 mb-2 rounded" />
      {/* Description placeholder */}
      <ShimmerSkeleton className="h-3 w-32 rounded" />
    </div>
  </div>
);

/**
 * Skeleton loader for a row of tool cards
 */
export const ToolCardsRowSkeleton = ({ count = 5 }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <ToolCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Skeleton loader for dashboard stats cards
 */
export const StatsCardSkeleton = () => (
  <div className="p-4 bg-white rounded-xl border border-gray-200 animate-pulse">
    <div className="flex items-center gap-3">
      <Skeleton className="w-10 h-10 rounded-lg" />
      <div className="flex-1">
        <Skeleton className="h-3 w-16 mb-2" />
        <Skeleton className="h-6 w-8" />
      </div>
    </div>
  </div>
);

/**
 * Skeleton loader for stats grid
 */
export const StatsGridSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
    {Array.from({ length: 4 }).map((_, i) => (
      <StatsCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Skeleton loader for a section with cards
 */
export const SectionSkeleton = ({ title = true, cardCount = 3 }) => (
  <div className="mb-8">
    {title && (
      <div className="flex items-center gap-2 mb-4">
        <Skeleton className="h-6 w-32" />
      </div>
    )}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: cardCount }).map((_, i) => (
        <div key={i} className="p-4 bg-white rounded-xl border border-gray-200 animate-pulse">
          <Skeleton className="h-4 w-3/4 mb-3" />
          <Skeleton className="h-3 w-full mb-2" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Full dashboard skeleton with shimmer effect
 */
export const DashboardSkeleton = () => (
  <div className="space-y-6 animate-in fade-in duration-300">
    {/* Header area skeleton */}
    <div className="flex items-center justify-between mb-6">
      <div>
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-64" />
      </div>
      <Skeleton className="h-10 w-32 rounded-lg" />
    </div>

    {/* Stats grid */}
    <StatsGridSkeleton />

    {/* Tool cards section */}
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-6 w-28" />
        <Skeleton className="h-8 w-24 rounded-lg" />
      </div>
      <ToolCardsRowSkeleton count={5} />
    </div>

    {/* Additional sections */}
    <SectionSkeleton cardCount={3} />
  </div>
);

/**
 * Quick Actions Grid Skeleton
 */
export const QuickActionsGridSkeleton = () => (
  <div className="space-y-6">
    {/* Search bar skeleton */}
    <div className="flex items-center gap-4 mb-4">
      <Skeleton className="h-10 flex-1 max-w-md rounded-lg" />
      <Skeleton className="h-10 w-32 rounded-lg" />
    </div>

    {/* Category sections */}
    {['Get Hired', 'Career Strategy', 'Learning'].map((_, index) => (
      <div key={index} className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Skeleton className="w-5 h-5 rounded" />
          <Skeleton className="h-5 w-24" />
        </div>
        <ToolCardsRowSkeleton count={4} />
      </div>
    ))}
  </div>
);

export default DashboardSkeleton;
